<template>
    <div class="s-pagination">
        <!-- 总数统计 -->
        <span
            v-if="showTotal"
            class="pagination-total"
            >共 {{ total }} 条记录</span
        >

        <!-- 分页按钮组 -->
        <div class="pagination-pager">
            <!-- 上一页 -->
            <SButton
                variant="ghost"
                size="icon"
                class="pager-arrow"
                :class="{ 'is-disabled': currentPage === 1 }"
                :disabled="currentPage === 1"
                @click="handlePrev"
            >
                <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2.5"
                        d="M15 19l-7-7 7-7"
                    />
                </svg>
            </SButton>

            <!-- 页码按钮 -->
            <template v-if="totalPages <= maxPagerCount">
                <button
                    v-for="page in totalPages"
                    :key="page"
                    class="pager-btn"
                    :class="{ 'is-active': currentPage === page }"
                    @click="handlePageChange(page)"
                >
                    {{ page }}
                </button>
            </template>
            <template v-else>
                <button
                    v-for="page in visiblePages"
                    :key="page"
                    class="pager-btn"
                    :class="{ 'is-active': currentPage === page }"
                    @click="handlePageChange(page)"
                >
                    {{ page }}
                </button>
                <span class="pager-ellipsis">...</span>
                <button
                    class="pager-btn"
                    :class="{ 'is-active': currentPage === totalPages }"
                    @click="handlePageChange(totalPages)"
                >
                    {{ totalPages }}
                </button>
            </template>

            <!-- 下一页 -->
            <SButton
                variant="ghost"
                size="icon"
                class="pager-arrow"
                :class="{ 'is-disabled': currentPage === totalPages }"
                :disabled="currentPage === totalPages"
                @click="handleNext"
            >
                <svg
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2.5"
                        d="M9 5l7 7-7 7"
                    />
                </svg>
            </SButton>
        </div>

        <!-- 每页条数选择器 -->
        <SSelect
            v-if="showSizeChanger"
            v-model="internalPageSize"
            :options="pageSizeOptions"
            custom-class="pagination-size-select"
            @update:model-value="handleSizeChange"
        />
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, watch } from 'vue'
    import SButton from '../button/index.vue'
    import SSelect from '../select/index.vue'

    defineOptions({ name: 's-pagination' })

    interface Props {
        /** 数据总数 */
        total: number
        /** 每页条数选项 */
        pageSizes?: number[]
        /** 最大显示页码数 */
        maxPagerCount?: number
        /** 是否显示总数 */
        showTotal?: boolean
        /** 是否显示每页条数选择器 */
        showSizeChanger?: boolean
    }

    const props = withDefaults(defineProps<Props>(), {
        pageSizes: () => [20, 50, 100],
        maxPagerCount: 5,
        showTotal: true,
        showSizeChanger: true,
    })

    const emit = defineEmits<{
        /** 页码变化事件 */
        change: [page: number]
        /** 每页条数变化事件 */
        sizeChange: [size: number]
    }>()

    // v-model 绑定
    const currentPage = defineModel<number>('currentPage', { default: 1 })
    const pageSize = defineModel<number>('pageSize', { default: 20 })

    // 内部每页条数（用于 select v-model）
    const internalPageSize = ref(pageSize.value)

    // 同步外部 pageSize 变化
    watch(
        () => pageSize.value,
        newVal => {
            internalPageSize.value = newVal
        }
    )

    /** 总页数 */
    const totalPages = computed(() => Math.ceil(props.total / pageSize.value) || 1)

    /** 每页条数选项（SSelect 格式） */
    const pageSizeOptions = computed(() =>
        props.pageSizes.map(size => ({
            label: `${size} 条/页`,
            value: size,
        }))
    )

    /** 可见页码列表 */
    const visiblePages = computed(() => {
        const pages: number[] = []
        const max = Math.min(props.maxPagerCount, totalPages.value - 1)
        for (let i = 1; i <= max; i++) {
            pages.push(i)
        }
        return pages
    })

    /** 上一页 */
    function handlePrev() {
        if (currentPage.value > 1) {
            currentPage.value--
            emit('change', currentPage.value)
        }
    }

    /** 下一页 */
    function handleNext() {
        if (currentPage.value < totalPages.value) {
            currentPage.value++
            emit('change', currentPage.value)
        }
    }

    /** 跳转到指定页 */
    function handlePageChange(page: number) {
        if (page !== currentPage.value && page >= 1 && page <= totalPages.value) {
            currentPage.value = page
            emit('change', page)
        }
    }

    /** 每页条数变更 */
    function handleSizeChange(newSize: string | number | undefined) {
        if (typeof newSize !== 'number') return
        internalPageSize.value = newSize
        pageSize.value = newSize
        currentPage.value = 1
        emit('sizeChange', newSize)
    }
</script>

<style scoped lang="scss">
    // 颜色变量
    $primary-500: var(--color-primary, #f97316);
    $gray-50: #f9fafb;
    $gray-100: #f3f4f6;
    $gray-200: #e5e7eb;
    $gray-300: #d1d5db;
    $gray-400: #9ca3af;
    $gray-500: #6b7280;
    $gray-600: #4b5563;
    $white: #fff;

    .s-pagination {
        display: flex;
        align-items: center;
        gap: 20px;
    }

    .pagination-total {
        flex: none;
        font-size: 14px;
        font-weight: 700;
        color: $gray-500;
    }

    .pagination-pager {
        display: flex;
        flex: 1;
        align-items: center;
        gap: 8px;
    }

    .pager-arrow {
        width: 32px;
        height: 32px;
        padding: 0;
        border-radius: 8px;
        color: $gray-400;

        &:hover:not(.is-disabled, :disabled) {
            color: $primary-500;
            background: rgba(249, 115, 22, 0.05);
        }

        &.is-disabled,
        &:disabled {
            color: $gray-200;
            cursor: not-allowed;
        }
    }

    .pager-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 36px;
        height: 36px;
        border: 2px solid $gray-100;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 900;
        color: $gray-400;
        background: transparent;
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover:not(.is-active) {
            border-color: $gray-200;
            color: $gray-600;
        }

        &.is-active {
            border-color: $primary-500;
            color: $primary-500;
            background: rgba(249, 115, 22, 0.03);
            box-shadow: 0 2px 4px rgba(249, 115, 22, 0.1);
        }
    }

    .pager-ellipsis {
        padding: 0 4px;
        font-size: 14px;
        font-weight: 900;
        letter-spacing: 2px;
        color: $gray-300;
    }

    :deep(.pagination-size-select) {
        width: 120px;

        :deep(.select-trigger) {
            padding: 6px 32px 6px 12px;
            border-color: $gray-200;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 700;
            color: $gray-500;

            &:hover,
            &:focus {
                border-color: rgba(249, 115, 22, 0.4);
            }
        }

        :deep(.select-options) {
            font-size: 12px;
        }
    }
</style>
